import _ from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {AsyncSelect} from '../../../ui-toolkit/indexExportV2';

import {loadEntityRaw} from '../../../ducks/entitydetail';
import {loadEntitiesRaw} from '../../../ducks/entitylist';
import {getFkOptionsSpecification} from '../../../ducks/fkoptionshelper';
import {getForeignKeyEntityDataType, getPathDetails} from '../../../ducks/formattersettingshelpers';
import {useTypeDetail} from '../../../ducks/typedetail-flattened';

function buildFilter(searchField, search, extraFilter) {
    return search
        ? extraFilter
            ? `${extraFilter} AND ${searchField} STRCON ${JSON.stringify(search)}`
            : `${searchField} STRCON ${JSON.stringify(search)}`
        : extraFilter;
}

function SearchableFKDropdownEditor({
    formatter,
    fieldName,
    entityType,
    parentTypeId,
    entityAttributeId,
    attributePath,
}) {
    const labelExpr = useMemo(
        () => _.template(formatter.settings.labelExpression),
        [formatter.settings.labelExpression],
    );
    const searchField = formatter.settings.searchField;

    const parentType = useTypeDetail(parentTypeId);
    const childDataType = getForeignKeyEntityDataType(entityType);

    const extraFilter = (() => {
        if (attributePath) {
            return getFkOptionsSpecification(parentType, childDataType, attributePath);
        }

        if (_.isNumber(entityAttributeId)) {
            const attribute = getPathDetails(parentType, entityAttributeId).finalAttribute;
            return getFkOptionsSpecification(parentType, childDataType, attribute.entityAttributeId);
        }

        throw new Error("[SearchableFKDropdownEditor] Either 'attributePath' or 'entityAttributeId' must be provided.");
    })();

    const loadOptions = useCallback(
        (search) =>
            loadEntitiesRaw({
                entityTypeId: childDataType.dataTypeId,
                sort: {field: searchField, dir: true},
                filter: buildFilter(searchField, search, extraFilter),
            }).then(({entities}) =>
                _.map(entities, (ent) => ({
                    value: ent.entityId,
                    label: labelExpr(ent),
                })),
            ),
        [childDataType.dataTypeId, searchField, extraFilter, labelExpr],
    );

    const loadSingleOption = useCallback(
        async (entityId) => {
            // empty string is the 'null' value in a formik form
            if (_.isEmpty(entityId) || _.isNil(entityId)) return '';
            return await loadEntityRaw(childDataType.dataTypeId, entityId).then((opt) => ({
                value: entityId,
                label: labelExpr(opt),
            }));
        },
        [childDataType.dataTypeId, labelExpr],
    );

    return (
        <AsyncSelect
            name={fieldName}
            // this loads the default values by calling loadOptions('')
            defaultOptions={true}
            loadOptions={loadOptions}
            loadOptionsForValues={loadSingleOption}
        />
    );
}

export default SearchableFKDropdownEditor;
